/* Copyright (c) 2021 MOCA Systems, Inc.  All rights reserved. */

import {
	Component,
	OnInit,
	AfterViewInit,
	OnDestroy,
	Input,
	Output,
	EventEmitter,
	ViewChild,
	ElementRef,
	ChangeDetectionStrategy,
	ChangeDetectorRef,
} from '@angular/core';
import * as Hammer from 'hammerjs';
import { BaseContainerComponent } from '../base-container/base-container.component';
import { ContainerLayerManager } from '../base-container/ContainerLayerManager';

export enum ResizableContainerDirections {
	NORTH = 'n',
	NORTH_EAST = 'ne',
	EAST = 'e',
	SOUTH_EAST = 'se',
	SOUTH = 's',
	SOUTH_WEST = 'sw',
	WEST = 'w',
	NORTH_WEST = 'nw',
}

@Component({
	selector: 'resizable-container',
	templateUrl: './resizable-container.component.html',
	styleUrls: ['./resizable-container.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ResizableContainerComponent
	extends BaseContainerComponent
	implements OnInit, AfterViewInit, OnDestroy
{
	@Input() public resizable = true;
	@Input() public directions = [
		ResizableContainerDirections.NORTH,
		ResizableContainerDirections.NORTH_EAST,
		ResizableContainerDirections.EAST,
		ResizableContainerDirections.SOUTH_EAST,
		ResizableContainerDirections.SOUTH,
		ResizableContainerDirections.SOUTH_WEST,
		ResizableContainerDirections.WEST,
		ResizableContainerDirections.NORTH_WEST,
	];
	@Input() public standalone = true; // add shadow etc. if it's standalone container

	@Output() public close: EventEmitter<any> = new EventEmitter();

	@ViewChild('container', { static: false }) public container: ElementRef;
	public isResizing: boolean;

	protected originalX: number;
	protected originalY: number;
	protected originalWidth: number;
	protected originalHeight: number;
	protected offsetX: number;
	protected offsetY: number;

	private direction: string;
	private resizeHandler: (MouseEvent) => void;
	private resizeEndHandler: () => void;

	constructor(
		protected changeDetectorRef: ChangeDetectorRef,
		protected containerLayerManager: ContainerLayerManager
	) {
		super(changeDetectorRef, containerLayerManager);

		this.y = (this.viewportHeight - this.height) / 2;
		this.x = (this.viewportWidth - this.width) / 2;
		// default to 10 to not interfere with browser window resize
		this.deadZoneSize = this.deadZoneSize || 10;

		this.resizeHandler = (event) => this.resize(event);
		this.resizeEndHandler = () => this.resizeEnd();

		Hammer.on(window, 'mousemove', this.resizeHandler);
		Hammer.on(window, 'mouseup', this.resizeEndHandler);
	}

	public ngOnInit() {}

	public ngAfterViewInit() {
		super.ngAfterViewInit();

		const style = getComputedStyle(this.container.nativeElement);
		this.borderTopBottom = parseFloat(style.borderTopWidth) + parseFloat(style.borderBottomWidth);
		this.borderLeftRight = parseFloat(style.borderLeftWidth) + parseFloat(style.borderRightWidth);
		this.minWidth = this.minWidth || this.width;
		this.minHeight = this.minHeight || this.height;
		this.x = this.x || this.deadZoneSize;
		this.y = this.y || this.deadZoneSize;
	}

	public ngOnDestroy() {
		this.changeDetectorRef.detach();
		this.destroy();
	}

	protected destroy(): void {
		super.destroy();

		Hammer.off(window, 'mousemove', this.resizeHandler);
		Hammer.off(window, 'mouseup', this.resizeEndHandler);
	}

	public resizeStart(event): void {
		const width = this.width || this.container.nativeElement.offsetWidth;
		const height = this.height || this.container.nativeElement.offsetHeight;

		this.isResizing = true;
		this.originalWidth = width;
		this.originalHeight = height;
		this.originalX = this.x;
		this.originalY = this.y;
		this.direction = event.currentTarget.attributes.direction.value;

		const boundingRect = this.container.nativeElement.getBoundingClientRect();
		switch (this.direction) {
			case ResizableContainerDirections.NORTH:
			case ResizableContainerDirections.NORTH_WEST:
			case ResizableContainerDirections.WEST:
				this.offsetX = event.clientX - boundingRect.left;
				this.offsetY = event.clientY - boundingRect.top;
				break;
			case ResizableContainerDirections.NORTH_EAST:
				this.offsetX = event.clientX - boundingRect.left - width;
				this.offsetY = event.clientY - boundingRect.top;
				break;
			case ResizableContainerDirections.EAST:
			case ResizableContainerDirections.SOUTH_EAST:
			case ResizableContainerDirections.SOUTH:
				this.offsetX = event.clientX - boundingRect.left - width;
				this.offsetY = event.clientY - boundingRect.top - height;
				break;
			case ResizableContainerDirections.SOUTH_WEST:
				this.offsetX = event.clientX - boundingRect.left;
				this.offsetY = event.clientY - boundingRect.top - height;
				break;
		}
	}

	private resize(event): void {
		if (!this.resizable || !this.isResizing) {
			return;
		}

		const mouseX = event.clientX - this.offsetX;
		const mouseY = event.clientY - this.offsetY;

		switch (this.direction) {
			case ResizableContainerDirections.NORTH:
				this.calcNorth(mouseY);
				break;
			case ResizableContainerDirections.NORTH_EAST:
				this.calcNorth(mouseY);
				this.calcEast(mouseX);
				break;
			case ResizableContainerDirections.EAST:
				this.calcEast(mouseX);
				break;
			case ResizableContainerDirections.SOUTH_EAST:
				this.calcSouth(mouseY);
				this.calcEast(mouseX);
				break;
			case ResizableContainerDirections.SOUTH:
				this.calcSouth(mouseY);
				break;
			case ResizableContainerDirections.SOUTH_WEST:
				this.calcSouth(mouseY);
				this.calcWest(mouseX);
				break;
			case ResizableContainerDirections.WEST:
				this.calcWest(mouseX);
				break;
			case ResizableContainerDirections.NORTH_WEST:
				this.calcNorth(mouseY);
				this.calcWest(mouseX);
				break;
		}

		this.changeDetectorRef.detectChanges();
	}

	private resizeEnd(): void {
		this.isResizing = false;
	}

	private calcNorth(mouseX: number): void {
		this.y = Math.min(
			Math.max(mouseX, this.deadZoneSize),
			this.y + (this.height || this.container.nativeElement.offsetHeight) - this.minHeight
		);
		this.height = Math.max(this.originalHeight + this.originalY - this.y, this.minHeight);
	}

	private calcEast(mouseX: number): void {
		this.width = Math.min(
			Math.max(mouseX - this.x, this.minWidth),
			this.viewportWidth - this.x - this.borderLeftRight - this.deadZoneSize
		);
	}

	private calcSouth(mouseY: number): void {
		this.height = Math.min(
			Math.max(mouseY - this.y, this.minHeight),
			this.viewportHeight - this.y - this.borderTopBottom - this.deadZoneSize
		);
	}

	private calcWest(mouseY: number): void {
		this.x = Math.min(
			Math.max(mouseY, this.deadZoneSize),
			this.x + (this.width || this.container.nativeElement.offsetWidth) - this.minWidth
		);
		this.width = Math.max(this.originalWidth + this.originalX - this.x, this.minWidth);
	}
}
